import { LegacyButton } from '../../navigation/LegacyButton';
import { AnchorTarget } from '../../../ts-types';
import { ZapCardSteps } from './ZapCardSteps';
import type { Step } from './ZapCardSteps';

import styles from './ZapCardDetails.module.scss';

type Props = {
  /**
   * The content to render for the details description.
   */
  children?: React.ReactNode;
  /**
   * The steps which comprise this Zap template.
   *
   * - `icon` is a URL representing an icon for the service used in this step.
   * - `id` is the ID of the service, e.g. "GoogleFormsAPI".
   * - `label` is the proper name of the trigger/action/search, e.g., "New Response in Spreadsheet".
   * - `name` is the name of the service, e.g., "Google Forms".
   */
  steps: Array<Step>;
  /**
   * `href` to a dedicated details page for the Zap template.
   */
  detailsHref?: string;
  /**
   * URL target. defaults to _self
   */
  detailsHrefTarget?: AnchorTarget;
  /**
   * `href` to use the Zap template.
   */
  editorHref?: string;
  /**
   * URL target. defaults to _self
   */
  editorHrefTarget?: AnchorTarget;
};

/**
 * Details for a `ZapCard` component, like Zap steps,
 * a description of it, CTAs, etc.
 */
export const ZapCardDetails = (props: Props) => {
  const target = props.detailsHrefTarget || '_self';
  const editorTarget = props.editorHrefTarget || '_self';

  return (
    <div className={styles.root} data-zds>
      <ZapCardSteps steps={props.steps} />
      {props.children && (
        <div className={styles.description} data-zds>
          {props.children}
        </div>
      )}
      <div>
        <div className={styles.actions} data-zds>
          {props.detailsHref && (
            <LegacyButton
              color="secondary"
              href={props.detailsHref}
              size="small"
              target={target}
            >
              See Zap details
            </LegacyButton>
          )}
          {props.editorHref && (
            <LegacyButton
              color="primary"
              hasShadow={true}
              href={props.editorHref}
              size="small"
              target={editorTarget}
            >
              Try it
            </LegacyButton>
          )}
        </div>
      </div>
    </div>
  );
};
